import { Icon } from '@chakra-ui/icons'
import {
  AspectRatio as ChakraAspectRatio,
  Badge,
  Box,
  Flex,
  HStack,
  Spacer,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react'
import numeral from 'numeral'
import React, { ReactElement, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFillShieldLockFill } from 'react-icons/bs'
import { RiDraftLine } from 'react-icons/ri'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import {
  getExtensionFromFileTypeAndMimeType,
  mediaLibraryThumbnailTransformation
} from '../../../../../utils/mediaLibrary'
import type { MediaLibrarySearchFileResponse } from '../../../../interfaces'
import { MediaLibraryActions, MediaLibraryActionType } from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import Hover from '../../../Hover'
import { ContextMenuTrigger } from '../../ContextMenu'
import CopyURLPopover from '../../CopyURLPopover'
import FilePreview from '../../FilePreview'
import { colors } from '../../../../../ui-kit'

const AspectRatio = styled(ChakraAspectRatio)`
  & > img,
  & > video {
    object-fit: contain !important;
  }
`
interface MediaLibrarySearchFileResponseWithSelection
  extends MediaLibrarySearchFileResponse {
  onSelect(idx: number, shiftKeyPressed: boolean): void
  idx: number
  isFileSelected: boolean
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  folderPath: string
  currentMediaCollection?: string
  mediaCollectionRoute?: boolean
}

const FileElement = (
  file: MediaLibrarySearchFileResponseWithSelection,
): ReactElement => {
  const {
    name,
    type,
    fileId,
    isPrivateFile,
    isPublished,
    size,
    height,
    width,
    fileType,
    url,
    updatedAt,
    versionInfo,
    idx,
    onSelect,
    isFileSelected,
    dispatch,
    folderPath,
    currentMediaCollection,
    mediaCollectionRoute,
  } = file

  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const history = useHistory()
  const { t } = useTranslation()
  const extension = getExtensionFromFileTypeAndMimeType(file)

  const redirectFileUrl = React.useMemo<string>((): string => {
    if (window.isMediaLibraryWidget) {
      const widgetHost = window.widgetHost

      return type === 'file-version'
        ? `/media-library/detail/${fileId}/${versionInfo.id}?widgetHost=${widgetHost}`
        : `/media-library/detail/${fileId}?widgetHost=${widgetHost}`
    }

    return type === 'file-version'
      ? `/media-library/detail/${fileId}/${versionInfo.id}`
      : `/media-library/detail/${fileId}`
  }, [
    type,
    fileId,
    versionInfo,
    window.isMediaLibraryWidget,
    window.widgetHost,
  ])

  const handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    e.preventDefault()
    if (onSelect) {
      onSelect(idx, e.shiftKey ? true : false)
    }
    dispatch([
      {
        type: MediaLibraryActions.SELECT_FILE,
        payload: {
          file,
          event: e,
        },
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
    ])
  }

  const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> = (event: React.MouseEvent<HTMLDivElement>) => {
    // Redirect to full screen view page
    if (window.isMediaLibraryWidget) {
      if (mediaCollectionRoute) {
        if (type === 'file-version') {
          history.push(
            `/media-collections/${currentMediaCollection}/detail/${fileId}/${versionInfo.id}?widgetHost=${window.widgetHost}`,
          )
        } else {
          history.push(
            `/media-collections/${currentMediaCollection}/detail/${fileId}?widgetHost=${window.widgetHost}`,
          )
        }
      } else if (type === 'file-version') {
        history.push(`/media-library/detail/${fileId}/${versionInfo.id}?widgetHost=${window.widgetHost}`)
      } else {
        history.push(`/media-library/detail/${fileId}?widgetHost=${window.widgetHost}`)
      }
    } else {
      if (mediaCollectionRoute) {
        if (type === 'file-version') {
          history.push(
            `/media-collections/${currentMediaCollection}/detail/${fileId}/${versionInfo.id}`,
          )
        } else {
          history.push(
            `/media-collections/${currentMediaCollection}/detail/${fileId}`,
          )
        }
      } else if (type === 'file-version') {
        history.push(`/media-library/detail/${fileId}/${versionInfo.id}`)
      } else {
        history.push(`/media-library/detail/${fileId}`)
      }
    }
  }

  return (
    <ContextMenuTrigger
      onTrigger={event => {
        if (!isFileSelected) {
          dispatch([
            {
              type: MediaLibraryActions.SELECT_FILE,
              payload: {
                file,
                event,
              },
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
        }
      }}
      dispatch={dispatch}
    >
      {contextMenuProps => (
        <VStack
          borderColor={isFileSelected ? 'appClearBlue' : 'appPaleLilac'}
          borderWidth="1px"
          spacing={0}
          cursor="pointer"
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          as={Link}
          to={redirectFileUrl}
          width="100%"
          overflow="hidden"
          justifyContent="flex-start"
          alignItems="flex-start"
          position="relative"
          {...contextMenuProps}
        >
          <Box width="100%" position="relative">
            <Flex
              overflow="hidden"
              justifyContent="flex-start"
              alignItems="center"
              background="appPaleGreyThree"
              paddingX={0.5}
              paddingY={0.5}
            >
              {extension ? (
                <Box
                  background="appPaleGreyThree"
                  paddingY={0.5}
                  paddingX={0.5}
                  fontSize="md"
                  textTransform="uppercase"
                >
                  {extension}
                </Box>
              ) : null}
              <Spacer />
              {(!isPublicLink && isPublished === false) ? (
                <Tooltip label={t('draft', 'Draft')}>
                  <Box
                    background="appPaleGreyThree"
                    paddingY={0.5}
                    paddingX={0.5}
                    fontSize="md"
                    textTransform="uppercase"
                  >
                    <Icon
                      mt="0.5"
                      as={RiDraftLine}
                      opacity="0.5"
                    />
                  </Box>
                </Tooltip>
              ) : null}
              {(!isPublicLink && isPrivateFile) ? (
                <Tooltip label={t('private', 'Private')}>
                  <Box
                    background="appPaleGreyThree"
                    paddingY={0.5}
                    paddingX={0.5}
                    fontSize="md"
                    textTransform="uppercase"
                  >
                    <Icon
                      mt="0.5"
                      as={BsFillShieldLockFill}
                      opacity="0.5"
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </Flex>

            <AspectRatio
              width="100%"
              ratio={1 / 1}
              borderColor="appPaleLilac"
              borderBottomWidth="1px"
            >
              <FilePreview
                file={file}
                folderPath={folderPath}
                transformation={mediaLibraryThumbnailTransformation}
              />
            </AspectRatio>

            {type === 'file-version' ? (
              <Tooltip
                label={versionInfo.name}
                placement="top-start"
                zIndex={9}
              >
                <Badge position="absolute" bottom="0" colorScheme="purple">
                  {t('file_version', 'FILE VERSION')}
                </Badge>
              </Tooltip>
            ) : null}
          </Box>

          <Hover w="full">
            {isHover => (
              <VStack
                spacing={1}
                paddingX={2}
                paddingY={1}
                width="100%"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Tooltip label={name} placement="top-start" zIndex={9}>
                  <Flex
                    width="100%"
                    color="appDarkIndigo"
                    fontWeight="semibold"
                    fontSize="md"
                  >
                    <Text className="asset-name" noOfLines={1} width="100%" color={colors.appDarkIndigo}>
                      {name}
                    </Text>
                  </Flex>
                </Tooltip>

                <HStack spacing={0} width="full" justifyContent="space-between">
                  <Text color="appSlate" fontSize="sm">
                    {numeral(size).format('0.00 ib').replace('i', '')}
                    {height && width ? ` • ${width}x${height}` : null}
                  </Text>
                  {isHover && (
                    <CopyURLPopover file={file} isHover={isHover} />
                  )}
                </HStack>
              </VStack>
            )}
          </Hover>
        </VStack>
      )}
    </ContextMenuTrigger>
  )
}

export default React.memo(FileElement)
